import type { CostpointAssignment, Tag, Transaction, VATRate } from '@finapp/types';
import type React from 'react';
import { TagSelector } from '../TagSelector';

interface CostpointAssignmentListProps {
  costpointAssignments: CostpointAssignment[];
  costpointTags: Record<number, Tag[]>;
  availableCostpoints: string[];
  vatRates: VATRate[];
  transactionVatId: Transaction['vat_id'];
  transactionDate: Transaction['date'];
  canEditCostpoints: boolean;
  isSuperAdmin: boolean;
  isReadOnly?: boolean;
  onTagChange: (costpointId: number, newTags: Tag[]) => void;
  onVatChange: (costpointId: number, newVatId: number) => void;
  onCostpointChange: (costpointId: number, newCostpoint: string) => void;
  onAmountChange: (costpointId: number, newAmount: number) => void;
}

export const CostpointAssignmentList: React.FC<CostpointAssignmentListProps> = ({
  costpointAssignments,
  costpointTags,
  availableCostpoints,
  vatRates,
  transactionVatId,
  transactionDate,
  canEditCostpoints,
  isSuperAdmin,
  isReadOnly = false,
  onTagChange,
  onVatChange,
  onCostpointChange,
  onAmountChange,
}) => {
  if (costpointAssignments.length === 0) {
    return (
      <div className="text-center py-4">
        <div className="text-sm text-gray-500">No costpoint assignments found</div>
        <div className="text-xs text-gray-400 mt-1">
          This transaction may need to be allocated to costpoints
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {costpointAssignments.map((costpoint, index) => (
        <div key={index} className="border rounded-lg p-3 bg-gray-50">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                {canEditCostpoints && !isReadOnly ? (
                  <select
                    value={costpoint.costpoint}
                    onChange={(e) => onCostpointChange(costpoint.id, e.target.value)}
                    className="text-sm font-medium text-gray-900 border border-gray-300 rounded px-2 py-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {availableCostpoints.map((cp) => (
                      <option key={cp} value={cp}>
                        {cp.toUpperCase()}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="text-sm font-medium text-gray-900">
                    {costpoint.costpoint?.toUpperCase()}
                  </span>
                )}
                {isSuperAdmin && !isReadOnly ? (
                  <input
                    type="number"
                    step="0.01"
                    className="text-sm font-medium text-gray-900 border border-gray-300 rounded px-1 py-0.5 w-24 text-right"
                    defaultValue={costpoint.amount?.toFixed(2)}
                    onBlur={(e) => {
                      const val = parseFloat(e.target.value);
                      if (!Number.isNaN(val) && val !== costpoint.amount) {
                        onAmountChange(costpoint.id, val);
                      }
                    }}
                  />
                ) : (
                  <span className="text-sm text-gray-600">€{costpoint.amount?.toFixed(2)}</span>
                )}
              </div>

              {/* Costpoint Tags Editor */}
              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Costpoint Tags
                </label>
                <TagSelector
                  selectedTags={costpointTags[costpoint.id] || []}
                  onTagsChange={(newTags) => onTagChange(costpoint.id, newTags)}
                  placeholder="Add tags for this costpoint..."
                  maxTags={1}
                  data-testid={`costpoint-${costpoint.id}-tag-selector`}
                />
              </div>

              {/* VAT Selector for Costpoint */}
              <div className="mt-3 flex items-center gap-2">
                <label className="text-xs font-medium text-gray-600">VAT:</label>
                <select
                  value={transactionVatId || ''}
                  onChange={(e) => {
                    const newVatId = e.target.value ? parseInt(e.target.value, 10) : null;
                    if (newVatId !== null) {
                      onVatChange(costpoint.id, newVatId);
                    }
                  }}
                  className="text-xs text-gray-900 border border-gray-300 rounded px-1 py-0.5 focus:ring-blue-500 focus:border-blue-500"
                  disabled={!canEditCostpoints || isReadOnly}
                >
                  <option value="">Inherited</option>
                  {vatRates.map((rate) => (
                    <option key={rate.id} value={rate.id}>
                      {rate.name} ({rate.rate}%)
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="text-xs text-gray-400">
              {transactionDate && new Date(transactionDate).toLocaleDateString()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
